// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

// Client mirror of the Lambda's units.py — the single source of truth for Khora unit conversion. A pinned
// length/area widget always measures in FEET (the gdb's projected US-survey-foot base; the whole-gdb
// snapshot AND the live view-scoped recompute are both feet) and then converts + formats here into the
// widget's requested display_unit, reproducing exactly what Khora reported in chat (units.py.value_label).
// Keep these factors, symbols, and decimals in lock-step with aw-khora/units.py.

export type MeasureDimension = 'length' | 'area';

// exact US survey foot (1200/3937 m), matching units.py — NOT the 0.3048 international foot.
const M_PER_SURVEY_FOOT = 1200 / 3937;

// unit -> [multiplier applied to a FEET value, display symbol, decimal places]
const LENGTH_UNITS: Record<string, [number, string, number]> = {
  ft: [1, 'ft', 0],
  m: [M_PER_SURVEY_FOOT, 'm', 2],
  cm: [M_PER_SURVEY_FOOT * 100, 'cm', 1],
  km: [M_PER_SURVEY_FOOT / 1000, 'km', 3],
  mi: [1 / 5280, 'mi', 3],
  yd: [1 / 3, 'yd', 1],
  in: [12, 'in', 1],
};

// unit -> [multiplier applied to a SQUARE-FEET value, display symbol, decimal places]
const AREA_UNITS: Record<string, [number, string, number]> = {
  sqft: [1, 'ft²', 0],
  sqm: [M_PER_SURVEY_FOOT ** 2, 'm²', 2],
  ha: [(M_PER_SURVEY_FOOT ** 2) / 1e4, 'ha', 3],
  ac: [1 / 43560, 'ac', 3],
};

const unitTable = (dimension: MeasureDimension): Record<string, [number, string, number]> => (
  dimension === 'area' ? AREA_UNITS : LENGTH_UNITS
);

// Whether `unit` is a known length unit (mirrors units.py.is_length_unit). Used to guard the aggregate
// path: an average/sum/min/max of a *_ft field carries a LENGTH display_unit over a feet base value, so a
// non-length unit must never be applied to it.
export const isLengthUnit = (unit?: string): boolean => !!unit && unit in LENGTH_UNITS;

// Convert + format a feet-based measurement into `unit`, matching units.py.format_measure exactly
// (comma-grouped, per-unit decimals, e.g. "9,400.98 m" / "1,234 ft"). `valueFt` is FEET for length,
// SQUARE FEET for area. An absent or unrecognized unit falls back to the feet base (ft / ft²), so a
// widget never mislabels a feet value with another unit's symbol.
export const formatMeasure = (valueFt: number, dimension: MeasureDimension, unit?: string): string => {
  const table = unitTable(dimension);
  const [multiplier, symbol, decimals] = (unit && table[unit]) || table[dimension === 'area' ? 'sqft' : 'ft'];
  const converted = valueFt * multiplier;
  const formatted = converted.toLocaleString('en-US', {
    minimumFractionDigits: decimals, maximumFractionDigits: decimals,
  });
  return `${formatted} ${symbol}`;
};
